import React, { useEffect, useMemo, useRef, useState } from "react";
import { address_book_save_api } from "../../service/api.js";
import "./CheckoutAddress.css";

// Billing and shipping addresses live in two address-book tables whose columns
// only differ by their prefix (B… / S…), so one set of components serves both.
export const ADDRESS_TYPES = {
  billing: {
    prefix: "B",
    idKey: "UserBillingAddressID",
    noun: "billing",
    hasContact: true,
  },
  shipping: {
    prefix: "S",
    idKey: "UserShippingAddressID",
    noun: "shipping",
    hasContact: false,
  },
};

const PAGE_SIZE = 20;

const FIELD_DEFS = [
  { key: "CustomerName", label: "Customer name", required: true, full: true },
  { key: "Address1", label: "Address line 1", required: true, full: true },
  { key: "Address2", label: "Address line 2 (optional)", full: true },
  { key: "City", label: "City / Suburb", required: true },
  { key: "State", label: "State", required: true },
  { key: "Zipcode", label: "Postcode", required: true },
  { key: "Country", label: "Country", required: true },
  { key: "Email", label: "Email", required: true, contactOnly: true },
  { key: "MobileNumber", label: "Mobile number", required: true, contactOnly: true },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const fieldValue = (type, address, key) =>
  (address && address[ADDRESS_TYPES[type].prefix + key]) ?? "";

export const getAddressId = (type, address) =>
  address ? address[ADDRESS_TYPES[type].idKey] : undefined;

// Everything a customer might type into the address-book search box
const searchText = (type, address) =>
  FIELD_DEFS.map((f) => fieldValue(type, address, f.key))
    .join(" ")
    .toLowerCase();

// ── A single address rendered as text ────────────────────────────────────────
export const AddressDetails = ({ type, address }) => {
  const v = (key) => fieldValue(type, address, key);
  const cityLine = [v("City"), v("State"), v("Zipcode")].filter(Boolean).join(" ");
  return (
    <>
      <p className="co-addr-name">{v("CustomerName")}</p>
      {v("Address1") && <p>{v("Address1")}</p>}
      {v("Address2") && <p>{v("Address2")}</p>}
      {cityLine && <p>{cityLine}</p>}
      {v("Country") && <p>{v("Country")}</p>}
      {ADDRESS_TYPES[type].hasContact && (v("Email") || v("MobileNumber")) && (
        <p className="co-addr-contact">
          {[v("Email"), v("MobileNumber")].filter(Boolean).join(" · ")}
        </p>
      )}
    </>
  );
};

// ── Modal shell ──────────────────────────────────────────────────────────────
const Dialog = ({ title, onClose, children, wide }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const previous = document.documentElement.style.overflowY;
    document.documentElement.style.overflowY = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.documentElement.style.overflowY = previous || "auto";
    };
  }, [onClose]);

  return (
    <div className="co-dialog-wrap">
      <div className="co-dialog-backdrop" onClick={onClose} />
      <div
        className={`co-dialog ${wide ? "co-dialog-wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="co-dialog-head">
          <h3>{title}</h3>
          <button type="button" className="co-dialog-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

// ── Add / edit form (saves straight into the address book) ───────────────────
const AddressFormDialog = ({ type, address, onClose, onSaved }) => {
  const cfg = ADDRESS_TYPES[type];
  const isEdit = !!address;
  const fields = FIELD_DEFS.filter((f) => !f.contactOnly || cfg.hasContact);

  const [values, setValues] = useState(() => {
    const initial = {};
    FIELD_DEFS.forEach((f) => {
      initial[f.key] = fieldValue(type, address, f.key);
    });
    if (!isEdit && !initial.Country) initial.Country = "Australia";
    return initial;
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState("");

  const validate = () => {
    const found = {};
    fields.forEach((f) => {
      const value = String(values[f.key] ?? "").trim();
      if (f.required && !value) found[f.key] = `${f.label} is required`;
    });
    if (!found.Zipcode && !/^\d{4,6}$/.test(String(values.Zipcode).trim())) {
      found.Zipcode = "Enter a valid postcode (4-6 digits)";
    }
    if (cfg.hasContact) {
      if (!found.Email && !EMAIL_PATTERN.test(String(values.Email).trim())) {
        found.Email = "Enter a valid email address";
      }
      if (!found.MobileNumber && String(values.MobileNumber).trim().length > 11) {
        found.MobileNumber = "Mobile number can be at most 11 characters";
      }
    }
    return found;
  };

  const handleChange = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const payload = {};
    fields.forEach((f) => {
      payload[cfg.prefix + f.key] = String(values[f.key] ?? "").trim();
    });

    setSaving(true);
    setServerError("");
    try {
      const res = await address_book_save_api(
        type,
        payload,
        isEdit ? getAddressId(type, address) : undefined
      );
      onSaved(isEdit ? getAddressId(type, address) : res?.Data?.[cfg.idKey] || "latest");
    } catch (error) {
      const message = error?.response?.data?.ErrorMessage;
      setServerError(
        typeof message === "string" ? message : "Unable to save the address. Please try again."
      );
      setSaving(false);
    }
  };

  return (
    <Dialog
      title={`${isEdit ? "Edit" : "Add new"} ${cfg.noun} address`}
      onClose={onClose}
    >
      <form className="co-form" onSubmit={handleSubmit} noValidate>
        <div className="co-form-grid">
          {fields.map((f) => (
            <div key={f.key} className={`co-field ${f.full ? "co-field-full" : ""}`}>
              <label htmlFor={`co-${type}-${f.key}`}>
                {f.label}
                {f.required && <span className="co-required"> *</span>}
              </label>
              <input
                id={`co-${type}-${f.key}`}
                type="text"
                value={values[f.key]}
                onChange={(e) => handleChange(f.key, e.target.value)}
                className={errors[f.key] ? "co-input-error" : ""}
                autoComplete="off"
              />
              {errors[f.key] && <span className="co-error">{errors[f.key]}</span>}
            </div>
          ))}
        </div>
        {!isEdit && (
          <p className="co-form-note">This address will be saved to your address book.</p>
        )}
        {serverError && <p className="co-error co-error-block">{serverError}</p>}
        <div className="co-dialog-actions">
          <button type="button" className="co-btn co-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="co-btn co-btn-primary" disabled={saving}>
            {saving ? "Saving..." : isEdit ? "Save changes" : "Save address"}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

// ── Address book picker with search ──────────────────────────────────────────
const AddressBookDialog = ({ type, addresses, selected, onSelect, onEdit, onAdd, onClose }) => {
  const cfg = ADDRESS_TYPES[type];
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const searchRef = useRef(null);

  useEffect(() => {
    if (searchRef.current) searchRef.current.focus();
  }, []);

  const sorted = useMemo(
    () =>
      [...addresses].sort((a, b) =>
        String(fieldValue(type, a, "CustomerName")).localeCompare(
          String(fieldValue(type, b, "CustomerName")),
          undefined,
          { sensitivity: "base" }
        )
      ),
    [addresses, type]
  );

  const filtered = useMemo(() => {
    const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return sorted;
    return sorted.filter((address) => {
      const haystack = searchText(type, address);
      return tokens.every((token) => haystack.includes(token));
    });
  }, [sorted, query, type]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [query]);

  const selectedId = getAddressId(type, selected);
  const shown = filtered.slice(0, visible);

  return (
    <Dialog title={`Choose ${cfg.noun} address`} onClose={onClose} wide>
      <div className="co-book-search">
        <input
          ref={searchRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, street, suburb, state, postcode..."
          aria-label="Search address book"
        />
        {query && (
          <button type="button" className="co-book-clear" onClick={() => setQuery("")}>
            Clear
          </button>
        )}
      </div>
      <p className="co-book-count">
        {query
          ? `${filtered.length} of ${addresses.length} addresses match "${query}"`
          : `${addresses.length} ${addresses.length === 1 ? "address" : "addresses"} in your address book`}
      </p>

      <div className="co-book-list">
        {shown.map((address) => {
          const id = getAddressId(type, address);
          const isSelected = String(id) === String(selectedId);
          return (
            <div
              key={id}
              className={`co-book-item ${isSelected ? "co-book-item-selected" : ""}`}
            >
              <div className="co-book-item-text">
                <AddressDetails type={type} address={address} />
              </div>
              <div className="co-book-item-actions">
                {isSelected ? (
                  <span className="co-badge">Selected</span>
                ) : (
                  <button
                    type="button"
                    className="co-btn co-btn-primary co-btn-small"
                    onClick={() => onSelect(address)}
                  >
                    Select
                  </button>
                )}
                <button type="button" className="co-link-btn" onClick={() => onEdit(address)}>
                  Edit
                </button>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p className="co-book-empty">
            {addresses.length === 0
              ? "Your address book is empty."
              : "No addresses match your search."}
          </p>
        )}
        {filtered.length > visible && (
          <button
            type="button"
            className="co-btn co-btn-secondary co-book-more"
            onClick={() => setVisible((n) => n + PAGE_SIZE)}
          >
            Show more ({filtered.length - visible} more)
          </button>
        )}
      </div>

      <div className="co-dialog-actions">
        <button type="button" className="co-btn co-btn-secondary" onClick={onClose}>
          Close
        </button>
        <button type="button" className="co-btn co-btn-primary" onClick={onAdd}>
          Add new {cfg.noun} address
        </button>
      </div>
    </Dialog>
  );
};

// ── One checkout section: current address card + Edit / Choose / Add ────────
// `mirror` shows another section's address read-only (shipping = billing).
export const AddressSection = ({
  type,
  title,
  addresses,
  selected,
  onSelect,
  onChanged,
  mirror,
  hasError,
  children,
}) => {
  const cfg = ADDRESS_TYPES[type];
  const [dialog, setDialog] = useState(null); // null | "book" | "add" | "edit"
  const [editing, setEditing] = useState(null);

  const shownAddress = mirror || selected;
  const shownType = mirror ? "billing" : type;

  const closeDialog = () => {
    setDialog(null);
    setEditing(null);
  };

  const handleSelect = (address) => {
    onSelect(address);
    closeDialog();
  };

  const handleSaved = async (id) => {
    closeDialog();
    await onChanged(type, id);
  };

  return (
    <section className="co-section">
      <h3 className="co-section-title">{title}</h3>
      <div className="co-section-body">
        <div className={`co-address-card ${hasError ? "co-address-card-error" : ""}`}>
          {shownAddress ? (
            <div className="co-address-text">
              <AddressDetails type={shownType} address={shownAddress} />
            </div>
          ) : (
            <p className="co-address-empty">
              No {cfg.noun} address yet.
              <br />
              Please add one to continue.
            </p>
          )}
        </div>

        {!mirror && (
          <div className="co-address-actions">
            {selected && (
              <button
                type="button"
                className="co-action"
                onClick={() => {
                  setEditing(selected);
                  setDialog("edit");
                }}
              >
                Edit
              </button>
            )}
            {addresses.length > 0 && (
              <button type="button" className="co-action" onClick={() => setDialog("book")}>
                Choose from address book
              </button>
            )}
            <button type="button" className="co-action" onClick={() => setDialog("add")}>
              Add new {cfg.noun} address
            </button>
          </div>
        )}
        {mirror && <p className="co-mirror-note">Same as billing address</p>}
      </div>
      {children}
      {hasError && (
        <p className="co-error">Please add or choose a {cfg.noun} address.</p>
      )}

      {dialog === "book" && (
        <AddressBookDialog
          type={type}
          addresses={addresses}
          selected={selected}
          onSelect={handleSelect}
          onEdit={(address) => {
            setEditing(address);
            setDialog("edit");
          }}
          onAdd={() => setDialog("add")}
          onClose={closeDialog}
        />
      )}
      {(dialog === "add" || dialog === "edit") && (
        <AddressFormDialog
          type={type}
          address={dialog === "edit" ? editing : null}
          onClose={closeDialog}
          onSaved={handleSaved}
        />
      )}
    </section>
  );
};
